<template>
  <section class="start-screen ui-container">
    <div class="start-screen__wrapper">
      <div
        v-if="isMobile"
        class="start-screen__picture-wrapper"
      >
        <UiPicture
          :data="images"
          width="280"
          height="182"
          mobile-stretch
          :is-lazy="false"
          class="start-screen__picture"
        />

        <template v-for="(plaque, i) in card.plaques">
          <UiTooltip
            v-if="plaque.tag && plaque.tagTooltipContent"
            :key="`${i}-tooltip`"
            :title="plaque.tagTooltipContent.title"
            :text="plaque.tagTooltipContent.text"
            :offset="[0, 14]"
            :delay="[0, 150]"
            :duration="[150, 110]"
            placement="bottom-start"
            class="start-screen__picture-tooltip-tag"
          >
            <UiTag
              :tag="plaque.tag"
              :bg-color="plaque.tagBgColor"
              :text-color="plaque.tagTextColor"
              :font-weight="plaque.tagFontWeight"
              size="medium"
            />
          </UiTooltip>

          <UiTag
            v-else-if="plaque.tag"
            :key="i"
            :tag="plaque.tag"
            :bg-color="plaque.tagBgColor"
            :text-color="plaque.tagTextColor"
            :font-weight="plaque.tagFontWeight"
            size="medium"
            class="start-screen__picture-tag"
          />
        </template>
      </div>

      <div class="start-screen__tags-wrapper">
        <UiCourseTag
          :tag="provider"
          :link="`${config.public.frontUrl}/vendor/${companySlug}/`"
          class="start-screen__institute-tag"
        />

        <UiRatingLink
          :rating="rating"
          :type="isMobile || rating.count < 10 ? 'short' : 'long'"
          size="medium"
          class="start-screen__tag"
        />
      </div>

      <h1
        class="h h--2 start-screen__title"
        v-html="title"
      />

      <p
        class="start-screen__description p p--2"
        v-html="description"
      />

      <div class="start-screen__price-wrapper">
        <SubscribeButtonSmart
          v-if="isSubscriptionAvailable"
          class="start-screen__subscribe-button"
        />

        <InfosList
          :infos="card.infos"
          class="start-screen__infos-list"
        />

        <CoursePrice
          :price="card.price"
          :program="card.program"
          :access-type="accessType"
          :is-subscription-available="isSubscriptionAvailable"
          tag-size="small"
          class="start-screen__price"
          @click:pay="$emit('click:pay', $event)"
        />

        <TermsLink
          v-if="accessType === AccessTypeEnum.Free"
          link="https://skillbox.ru/legal-docs/chou/file/frimium_academika.pdf"
          class="start-screen__terms"
        >
          Условия акции
        </TermsLink>

        <TermsLink
          v-else-if="isSubscriptionAvailable"
          link="https://skillbox.ru/legal-docs/chou/file/subscribe-pro.pdf"
          class="start-screen__terms"
        >
          Условия подписки
        </TermsLink>
      </div>

      <UiButtonLink
        link="#program"
        theme="secondary"
        size="big"
        class="start-screen__button"
      >
        Посмотреть программу
      </UiButtonLink>
    </div>
  </section>
</template>

<script setup lang="ts">
  import UiPicture from '@academica-box/components/Media/UiPicture/UiPicture.vue'
  import UiRatingLink from '@academica-box/components/Links/UiRatingLink/UiRatingLink.vue'
  import UiButtonLink from '@academica-box/components/Links/UiButtonLink/UiButtonLink.vue'
  import UiCourseTag from '@academica-box/components/Plaques/UiCourseTag/UiCourseTag.vue'
  import UiTag from '@academica-box/components/Plaques/UiTag/UiTag.vue'
  import UiTooltip from '@academica-box/components/InteractiveElements/UiTooltip/UiTooltip.vue'

  import type { Images } from '@academica-box/components/Media/UiPicture/types'
  import type { CourseTag } from '@academica-box/components/Plaques/UiCourseTag/types'
  import type { RatingLink } from '@academica-box/components/Links/UiRatingLink/types'
  import type { CourseCardBlock } from '@academica-box/helpers/mappingCourseData'
  import SubscribeButtonSmart from '@/components/PromoButton/SubscribeButton.smart/SubscribeButton.smart.vue'
  import TermsLink from '@/components/TermsLink/TermsLink.vue'
  import CoursePrice from '@/components/Price/CoursePrice/CoursePrice.vue'
  import InfosList from '@/components/InfosList/InfosList.vue'

  import { AccessTypeEnum } from '@/helpers/mappingCourseData'

  defineProps<{
    images: Images;
    provider: CourseTag;
    rating: RatingLink;
    title: string;
    description: string;
    card: CourseCardBlock['card'],
    accessType: AccessTypeEnum;
    companySlug: string;
    isSubscriptionAvailable: boolean;
  }>()

  defineEmits(['click:pay'])

  const config = useRuntimeConfig()
  const viewport = useViewport()
  const isMobile = computed(() => viewport.isLessThan('lg'))
</script>

<style lang="scss">
  @use '@academica-box/utils/styles/mixins/UiMedia' as *;
  @use '@academica-box/utils/styles/mixins/UiReset' as *;

  .start-screen {
    @include media(lg) {
      position: relative;
    }
  }

  .start-screen__wrapper {
    @include media(lg) {
      width: 608px;
    }

    @include media(xl) {
      width: 672px;
    }

    @include media(xxl) {
      width: 848px;
    }
  }

  .start-screen__picture-wrapper {
    display: grid;
    margin-bottom: 32px;
    border-radius: 16px;
    overflow: hidden;

    @include media(md) {
      width: fit-content;
    }
  }

  .start-screen__picture {
    grid-row: 1/1;
    grid-column: 1/1;

    @include media(sm-max) {
      padding-bottom: calc(100% / 4 * 2.6);
    }

    @include media(md) {
      width: 335px;
      height: 218px;
    }
  }

  .start-screen__picture-tag,
  .start-screen__picture-tooltip-tag {
    z-index: 1;
    grid-row: 1/1;
    grid-column: 1/1;
    align-self: flex-start;
    justify-self: flex-start;
    margin: 10px;
  }

  .start-screen__picture-tooltip-tag {
    cursor: pointer;
  }

  .start-screen__tags-wrapper {
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    @include media(lg) {
      margin-bottom: 24px;
    }
  }

  .start-screen__institute-tag {
    margin-right: 6px;
  }

  .start-screen__title {
    margin-bottom: 16px;

    @include media(lg) {
      margin-bottom: 24px;
    }
  }

  .start-screen__description {
    margin: 0;
    color: var(--text-secondary-color);

    @include media(lg) {
      margin-bottom: 24px;
    }
  }

  .start-screen__price-wrapper {
    margin-bottom: 48px;

    @include media(lg) {
      display: none;
    }
  }

  .start-screen__subscribe-button {
    margin-top: 24px;
  }

  .start-screen__infos-list {
    margin-bottom: 4px;

    @include media(md) {
      margin-bottom: 8px;
    }
  }

  .start-screen__button {
    @include media(md-max) {
      display: none;
    }
  }

  .start-screen__terms {
    margin-top: 20px;
  }

  .start-screen__list {
    display: grid;
    grid-gap: 23px 20px;
    grid-template-columns: repeat(2, 1fr);
    margin: 0;

    @include media(md) {
      grid-gap: 35px 0;
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .start-screen__item {
    &:nth-child(n + 4) {
      @include media(lg) {
        display: none;
      }
    }
  }
</style>
